import React, { useEffect, useState, useRef } from 'react';
import Box from '@mui/material/Box';
import Viewer from 'viewerjs';
import 'viewerjs/dist/viewer.min.css';

// Frosted-glass surface shared by the zoom pill and the rotate button, in the
// spirit of the Apple Maps controls: a translucent, blurred panel that floats
// over the document rather than a solid chip.
const glass = {
  backgroundColor: 'rgba(255, 255, 255, 0.62)',
  backdropFilter: 'blur(18px) saturate(180%)',
  WebkitBackdropFilter: 'blur(18px) saturate(180%)',
  border: '1px solid rgba(255, 255, 255, 0.55)',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.18)',
  color: '#1d1d1f',
};

const style = {
  root: {
    position: 'relative',
    backgroundColor: '#eaeaea',
    '> img': {
      visibility: 'hidden',
    },
  },
  // The controls float over the viewer canvas. The wrapper itself is
  // click-through so dragging to pan the document still reaches the canvas;
  // only the pill and button capture pointer events.
  controls: {
    position: 'absolute',
    bottom: '16px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    zIndex: 5,
    pointerEvents: 'none',
    '& > *': { pointerEvents: 'auto' },
  },
  // One pill holds both zoom controls: minus on the left, plus on the right,
  // split by a hairline divider.
  zoomPill: {
    ...glass,
    display: 'flex',
    alignItems: 'center',
    height: '40px',
    borderRadius: '999px',
    overflow: 'hidden',
  },
  zoomButton: {
    width: '48px',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: 'inherit',
    transition: 'background-color 0.15s',
    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.06)' },
    '&:active': { backgroundColor: 'rgba(0, 0, 0, 0.12)' },
    '&:disabled': { opacity: 0.45, cursor: 'default' },
  },
  divider: {
    width: '1px',
    height: '22px',
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
    flexShrink: 0,
  },
  // Rotate lives on its own, detached from the zoom pill.
  rotateButton: {
    ...glass,
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.8)' },
    '&:active': { backgroundColor: 'rgba(255, 255, 255, 0.92)' },
    '&:disabled': { opacity: 0.45, cursor: 'default' },
  },
};

const MinusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const RotateLeftIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M3 3v6h6M3.6 15a9 9 0 1 0 .9-6.7L3 9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const uid = () => Math.round(Math.random() * 1000000).toString();

const ImagePreview = ({ id = uid(), src, height = '360px' }) => {
  const [viewer, setViewer] = useState(null);
  // viewer.js's zoom()/rotate() are no-ops until the inline viewer has finished
  // rendering the image (its "viewed" state), so we gate the controls on this
  // until then and disable the buttons so an early click can't silently do
  // nothing. Once viewed, the viewer stays initialised across src updates.
  const [ready, setReady] = useState(false);
  const rootRef = useRef();

  useEffect(() => {
    // We render our own controls (below), so viewer.js's built-in toolbar is
    // turned off and we drive it through its zoom()/rotate() methods instead.
    const v = new Viewer(document.getElementById(id), {
      title: false,
      inline: true,
      backdrop: false,
      fullscreen: false,
      navbar: false,
      toolbar: false,
      // Without a transition, viewer.js fires `viewed` synchronously once the
      // image renders; with one it waits for a transitionend that never comes
      // for zero-size sources (e.g. an SVG with no intrinsic size), which would
      // leave our controls stuck disabled. Instant zoom/rotate also suits a
      // document inspector.
      transition: false,
      viewed: () => setReady(true),
    });
    setViewer(v);

    const sizeListener = setInterval(() => {
      if (
        rootRef.current &&
        v &&
        v.viewer &&
        v.onResize &&
        (rootRef.current.clientHeight !== v.viewer.clientHeight || rootRef.current.clientWidth !== v.viewer.clientWidth)
      ) {
        v.onResize();
      }
    }, 1000);

    return () => {
      v && v.destroy();
      clearInterval(sizeListener);
    };
  }, []);

  useEffect(() => {
    viewer && viewer.update();
  }, [src]);

  const zoomOut = () => ready && viewer && viewer.zoom(-0.3);
  const zoomIn = () => ready && viewer && viewer.zoom(0.3);
  const rotateLeft = () => ready && viewer && viewer.rotate(-90);

  return (
    <Box ref={rootRef} sx={{ ...style.root, height, minHeight: '320px' }}>
      <img id={id} src={src} height="100%" width="100%" />
      <Box sx={style.controls}>
        <Box sx={style.zoomPill}>
          <Box
            component="button"
            type="button"
            aria-label="Zoom out"
            disabled={!ready}
            sx={style.zoomButton}
            onClick={zoomOut}
          >
            <MinusIcon />
          </Box>
          <Box sx={style.divider} />
          <Box
            component="button"
            type="button"
            aria-label="Zoom in"
            disabled={!ready}
            sx={style.zoomButton}
            onClick={zoomIn}
          >
            <PlusIcon />
          </Box>
        </Box>
        <Box
          component="button"
          type="button"
          aria-label="Rotate left"
          disabled={!ready}
          sx={style.rotateButton}
          onClick={rotateLeft}
        >
          <RotateLeftIcon />
        </Box>
      </Box>
    </Box>
  );
};

export default ImagePreview;
